import React, {useEffect, useState, useRef, useMemo} from 'react';
import { useHistory, useParams,useLocation } from 'react-router-dom';
import { Box, Typography, Grid, IconButton, Avatar, CircularProgress, Tooltip } from '@mui/material';
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { LinearProgress } from '@mui/material';
import {getInsiderAssessmentPreviewService} from "../../../services/assessments.service";
import {COS_PREVIEW_CODE} from "../../../utils/cpci.util";
import {updateUserDetailsService, getUserDetailsService} from "../../../services/auth.service";
import { useAuth } from "../../../context/auth";
import { DQ_PRO_PLAN_KEY, ASSESSMENT_TYPE, USER_GROUPS } from "../../../utils/constants";
import { solutionPlanKey } from '../../../utils/productAccess';
import { planHasSequentialSteps, isClientOnboardingUser, mergeOnboardingRefreshIntoAuthUser } from "../DQProV2/constants/onboardingSteps.config";
import Button from "@mui/material/Button";
import CheckIcon from '@material-ui/icons/Check';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import RazProgressSVG from '../../../components/UI/SVG_IMGs/RazProgressSVG';
import '../../../assets/css/glossaryTerm.css';

// Brand primary accent (falls back to DQ orange).
const ACCENT = 'var(--brand-primary, #FF6900)';

// ── ui_rules (ported from my-dq-web's AssessmentSlide) ─────────────────────
// Question/answer data can carry a `ui_rules` array that layers extra
// behavior onto a question without a new question TYPE:
//   INDEX_EFFECT / USER_INPUT — that option index is an inline text field
//     instead of a clickable row.
//   INDEX_EFFECT / UNSELECT   — selecting that option (MAQ) clears every
//     other pick, and picking anything else clears it.
//   QUESTION_EFFECT / TOOLTIP — wraps a keyword in the question text with a
//     hoverable tooltip.
//   ANSWER_EFFECT / TOOLTIP   — same, but for a keyword inside choice text.
const parseUiRules = (rawRules) => {
    if (!rawRules) return [];
    try {
        const value = typeof rawRules === 'string' ? JSON.parse(rawRules) : rawRules;
        return Array.isArray(value) ? value : [];
    } catch (e) {
        console.error('Failed to parse ui_rules JSON:', rawRules, e);
        return [];
    }
};

// Splits `text` (a plain string, or an already-segmented array from a prior
// rule) into a new segment array, replacing every case-insensitive match of
// rule.text with a `{ tooltip, label }` marker. Building segments directly
// (rather than an HTML string later parsed by a library) avoids needing an
// HTML-parsing dependency dq-seal-client doesn't otherwise have.
const applyTooltipRule = (text, rule) => {
    if (!rule?.text) return Array.isArray(text) ? text : [text];
    const segments = Array.isArray(text) ? text : [text];
    const needle = rule.text.toLowerCase();
    const result = [];
    segments.forEach((seg) => {
        if (typeof seg !== 'string' || !seg) { if (seg) result.push(seg); return; }
        const lower = seg.toLowerCase();
        let cursor = 0;
        let idx = lower.indexOf(needle, cursor);
        while (idx !== -1) {
            if (idx > cursor) result.push(seg.slice(cursor, idx));
            result.push({ tooltip: rule.html, label: seg.slice(idx, idx + rule.text.length) });
            cursor = idx + rule.text.length;
            idx = lower.indexOf(needle, cursor);
        }
        if (cursor < seg.length) result.push(seg.slice(cursor));
    });
    return result;
};

// ── Section-page client-side copy ─────────────────────────────────────────────
// The API sends per-section DATA only (number, title, description, component
// names, question_count); all repeated template text lives here.

// Short blurbs per section component (Assess L4 name → one-liner). Components
// without an entry render with no blurb.
const SECTION_COMPONENT_DESCRIPTIONS = {
    'Digital Access at Home and School': 'Devices and internet you can use',
    'Youth AI-Digital Activities': 'What you do online',
    'Self-Regulated AI-Digital Engagement': 'Balancing your screen time',
    'Authentic and Ethical Digital Identity': 'How you show who you are online',
    'Digital Hygiene and Privacy Management': 'Keeping your info private and safe',
    'Critical Information Management': 'Telling what is true online',
    'Content Risk Avoidance': 'Handling upsetting content',
    'Contact Risk Avoidance': 'Dealing with strangers online',
    'Cyberbullying Management': 'Handling unkind behavior online',
    'Youth Perception of Home and Support Environment': 'Support from family and trusted adults',
    'Youth Perception of Parental Mediation': 'How parents guide your online use',
    'Youth Perception of School AI-Digital Literacy Education': 'What school teaches about digital life',
};

// LC-plan preview: 14 fixed questions, 2 per section across 7 sections, shown
// in this exact order — everyone else (P0, P1, …) still sees the full set.
// Matched against each raw instrument's own raw_data_code.
const LC_PREVIEW_QUESTION_CODES = [
    ['dqworld.q3', 'dqworld.q1'],
    ['air.dqworld.q6', 'air.dqworld.q16'],
    ['amf.q26', 'cpci26.airisks.05'],
    ['dqworld.q36', 'dqworld.q314'],
    ['dqworld.q259', 'dqworld.q218'],
    ['dqtest.q79', 'cpci26.airisks.03'],
    ['aimc.q3', 'dqworld.q1023'],
];

/**
 * Reduce the raw (unmapped) instruments list to the LC whitelist, keeping
 * each kept section's own section_intro card ahead of its two questions.
 * The intro card isn't tagged with a raw_data_code, so it's found by walking
 * backward from the first kept question in that section to the nearest
 * section_intro item — the same interleaving order the backend already sends.
 * A whitelist code missing from this particular assessment's data is skipped
 * rather than guessed at.
 */
const filterInstrumentsForLcPreview = (instruments) => {
    const result = [];
    LC_PREVIEW_QUESTION_CODES.forEach((codes) => {
        const firstIdx = instruments.findIndex(
            (it) => it.type !== 'section_intro' && codes.includes(it.raw_data_code),
        );
        if (firstIdx === -1) return;
        for (let i = firstIdx; i >= 0; i--) {
            if (instruments[i].type === 'section_intro') {
                result.push(instruments[i]);
                break;
            }
        }
        codes.forEach((code) => {
            const item = instruments.find((it) => it.type !== 'section_intro' && it.raw_data_code === code);
            if (item) result.push(item);
        });
    });
    return result;
};

// Section note template — question count from the API, time estimated client-side
// (~0.2–0.3 min per question, matching the assessment's overall 40–50 min / 179 q).
const sectionNoteText = (questionCount) => {
    const n = Number(questionCount) || 0;
    if (n <= 0) return 'Your answers are confidential and will help us understand your digital citizenship skills.';
    const lo = Math.max(1, Math.round(n * 0.2));
    const hi = Math.max(lo + 1, Math.ceil(n * 0.3));
    return `This section contains ${n} question${n === 1 ? '' : 's'} and takes about ${lo} - ${hi} minutes to complete. Your answers are confidential and will help us understand your digital citizenship skills.`;
};

const useStyles = makeStyles((theme) => ({
    title: {
        background: 'linear-gradient(to right, #6A8EC9 10%, #8C74A7 35%, #D44C71 59%, #C91C6E 100%)',
        color: 'transparent',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: '500 !important',
        textAlign: 'center',
        fontSize: '45px !important',
        [theme.breakpoints.down('sm')]: {
            fontSize: "28px !important",
        },
    },
    questionContainer: {
        position: 'relative',
        minHeight: '400px',
        padding: theme.spacing(4),
        borderRadius: '8px',
        maxWidth: '1200px',
        width: '100%',
        margin: '0 auto',
        [theme.breakpoints.down('md')]: {
            marginTop: 'auto',
            marginBottom: 'auto',
            paddingLeft: '80px !important',
            paddingRight: '80px !important',
        },
        [theme.breakpoints.between('lg', 'md')]: {
            maxWidth: '890px',
        },
        [theme.breakpoints.up('xl')]: {
            maxWidth: '1200px',
        },

    },
    questionText: {
        textAlign: 'center',
        color: '#444 !important',
        fontSize: '1.9rem !important',
        fontWeight: '700 !important',
        lineHeight:'1 !important',
        marginBottom: '32px !important',
    },
    answerContainer: {
        // position: 'fixed',
        // bottom: '10%',
        height: '42%',
        marginTop:'50px',
        paddingBottom: '4vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflowY: 'auto',
        [theme.breakpoints.down('md')]: {
            marginTop:'0px',
        },
    },
    optionItemBase: {
        borderRadius: '8px',
        border: '2px solid #fe696e',
        cursor: 'pointer',
        transition: '0.2s all',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        boxSizing: 'border-box',
        minHeight: '64px',
        flex: 1,
    },

    maqOption: {
        flex: 1,
        width: '100%',
        textAlign: 'center',
        overflow: 'auto',
        [theme.breakpoints.down('sm')]: {
            minWidth: '200px !important',
            fontSize: '0.9rem',
            height: 'auto',
        },
    },


    lsqOption: {
        flex: 1,
        textAlign: 'center',
        padding: '16px',
        minHeight: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        boxSizing: 'border-box',
        minWidth:'210px',
    },

    opqOption: {
        flex: 0.2,
        textAlign: 'center',
        padding: '16px',
        minHeight: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        boxSizing: 'border-box',
    },

    ynOption: {
        minWidth: '120px',
        [theme.breakpoints.down('sm')]: {
            minWidth: '100px !important',
            fontSize: '0.85rem',
        },
    },

    selectedOption: {
        backgroundColor: '#fe696e',
        color: '#fff',
        borderColor: '#fe696e',
        '&:hover': {
            backgroundColor: '#fe696e',
        },
    },
    centeredOption: {
        margin: '0 auto',
    },
    navigationContainer: {
        position: 'absolute',
        top: '50%',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        pointerEvents: 'none',
        zIndex: 1000,
        maxWidth: '1555px',
        left: '50%',
        transform: 'translateX(-50%) translateY(-50%)',
        [theme.breakpoints.up('lg')]: {
            top: '53%',
            width: '97%',
            left: '50%',
        },
    },
    navButton: {
        pointerEvents: 'auto',
        backgroundColor: 'transparent',
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            width: '65px',
            height: '65px',
            borderRadius: '50%',
            backgroundColor: '#fe696e',
            zIndex: -1,
            top: '50%',
            left: '45%',
            transform: 'translate(-50%, -50%)',
        },
        '&:focus': {
            outline: 'none',
            border: 'none',
            boxShadow: 'none',
        },
        '&.Mui-disabled': {
            opacity: 0.3,
        },
        [theme.breakpoints.down('sm')]: {
            width: '40px',
            height: '40px',
            '&::before': {
                width: '44px',
                height: '44px',
            }
        }
    },
    navIcon: {
        color: '#fff',
        fontSize: '40px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '25px',
        }
    },
    questionTypeIndicator: {
        backgroundColor: '#f0f0f0',
        padding: theme.spacing(1, 2),
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: 500,
        marginBottom: theme.spacing(2),
        display: 'inline-block',
    },
    progressBar: {
        width:'55% !important',
        margin:'15px auto',
        height: '13px !important',
        borderRadius: '13px',
        border:'1px solid #fe696e',
        backgroundColor:'#ffffff !important',
        '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(to right, #E7466E, #FFAC46)',
        },
    },
    questionSubtitleTop: {
        color: '#fe696e',
        fontSize: '30px !important',
        fontWeight: "bold",
        textAlign:'center',
        lineHeight:'1.1 !important',
        marginBottom: '32px !important',
    },

    questionSubtitleBottom: {
        color: '#0097b2',
        fontSize: '30px !important',
        lineHeight:'1.1 !important',
        marginTop: '10px',
        fontWeight: "bold",
        textAlign:'center'
    },

}));


const DQProPreviewAssessment = ({ embedded = false }) => {
    const classes = useStyles();
    const location = useLocation();
    const history = useHistory();
    const { authUser, setAuthUser } = useAuth();
    const assessmentData = location.state?.assessment;
    const { code } = useParams();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(true);

    // MTQ: drag-and-drop reordering via pointer events (works for mouse + touch,
    // no DnD library needed). rowRefs holds each row's DOM node so pointermove
    // can tell which row the dragged item is currently over.
    const rowRefs = useRef([]);
    const dragInfo = useRef({ index: null });
    const [dragIndex, setDragIndex] = useState(null);

    // ui_rules: current question's parsed rules, the option indices they flag
    // as a custom-input field or an "unselect everything else" trigger, and
    // floating-tooltip state for the QUESTION_EFFECT/ANSWER_EFFECT rules.
    const currentQuestionForRules = questions[currentQuestionIndex];
    const uiRules = useMemo(
        () => parseUiRules(currentQuestionForRules?.ui_rules),
        [currentQuestionForRules]
    );
    const inputIndices = useMemo(
        () => uiRules.filter(r => r.type === 'INDEX_EFFECT' && r.effect === 'USER_INPUT').map(r => r.index),
        [uiRules]
    );
    // UNSELECT-rule indices are recomputed fresh inside handleOptionSelect's
    // setQuestions updater instead of reused from here, so they always match
    // the specific question object React is updating rather than whatever
    // was in scope when this render happened.
    const processedQuestionSegments = useMemo(() => {
        const base = currentQuestionForRules?.instrument_question || currentQuestionForRules?.question || '';
        return uiRules
            .filter(r => r.type === 'QUESTION_EFFECT' && r.effect === 'TOOLTIP')
            .reduce((text, rule) => applyTooltipRule(text, rule), base);
    }, [currentQuestionForRules, uiRules]);
    const processedChoiceSegments = useMemo(() => {
        const options = currentQuestionForRules?.options || [];
        const tooltipRules = uiRules.filter(r => r.type === 'ANSWER_EFFECT' && r.effect === 'TOOLTIP');
        return options.map(option => tooltipRules.reduce((text, rule) => applyTooltipRule(text, rule), option));
    }, [currentQuestionForRules, uiRules]);

    const [tooltipText, setTooltipText] = useState('');
    const [displayTooltip, setDisplayTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const onHoverTooltip = (event, html) => {
        setTooltipPos({ x: event.clientX, y: event.clientY });
        setTooltipText(html || '');
        setDisplayTooltip(true);
    };
    const onExitHoverTooltip = () => setDisplayTooltip(false);

    // Renders a plain string or a tooltip-segment array (see applyTooltipRule)
    // as React nodes — plain text runs through untouched, `{tooltip, label}`
    // markers become a hoverable span that opens the floating tooltip box.
    const renderTextSegments = (value) => {
        const segments = Array.isArray(value) ? value : [value];
        return segments.map((seg, i) => (typeof seg === 'string'
            ? <React.Fragment key={i}>{seg}</React.Fragment>
            : (
                <Box
                    key={i}
                    component="span"
                    onMouseEnter={(e) => onHoverTooltip(e, seg.tooltip)}
                    onMouseLeave={onExitHoverTooltip}
                    sx={{ textDecoration: 'underline dotted', textUnderlineOffset: '3px', cursor: 'help', color: ACCENT, fontWeight: 700 }}
                >
                    {seg.label}
                </Box>
            )
        ));
    };

    // `questions` also holds interleaved section_intro cards, which aren't
    // real questions — the "Question NN of total" display below must count
    // (and number) only the actual questions, not those intro slides.
    const realQuestionTotal = questions.filter(q => q.type !== 'section_intro').length;
    const realQuestionNumber = questions
        .slice(0, currentQuestionIndex + 1)
        .filter(q => q.type !== 'section_intro').length;

    // "None of the above" / "all of the above" (and the common punctuation-free
    // variant) are exclusive within a MAQ: picking one clears every other pick,
    // and picking anything else clears any exclusive option already held.
    const isExclusiveMaqOption = (option) => /none of the above|all of the above/i.test(String(option || ''));

    // `optionIndex` is only used for the rule-driven MAQ unselect check below —
    // everything else still identifies options by value, unchanged.
    const handleOptionSelect = (option, optionIndex) => {
        setQuestions(prevQuestions => {
            return prevQuestions.map((question, index) => {
                if (index !== currentQuestionIndex) return question;

                const updatedQuestion = { ...question };

                if (question.type === 'MAQ') {
                    const prev = Array.isArray(question.userAnswer) ? question.userAnswer : [];
                    const rules = parseUiRules(question.ui_rules);
                    if (prev.includes(option)) {
                        updatedQuestion.userAnswer = prev.filter(item => item !== option);
                    } else if (rules.length > 0) {
                        // Rule-driven: an UNSELECT-flagged option clears every other
                        // pick; picking anything else clears any UNSELECT option held.
                        const unselectRuleIndices = rules
                            .filter(r => r.type === 'INDEX_EFFECT' && r.effect === 'UNSELECT')
                            .map(r => r.index);
                        if (unselectRuleIndices.includes(optionIndex)) {
                            updatedQuestion.userAnswer = [option];
                        } else {
                            const optionsList = question.options || [];
                            updatedQuestion.userAnswer = [
                                ...prev.filter(item => !unselectRuleIndices.includes(optionsList.indexOf(item))),
                                option,
                            ];
                        }
                    } else if (isExclusiveMaqOption(option)) {
                        updatedQuestion.userAnswer = [option];
                    } else {
                        updatedQuestion.userAnswer = [...prev.filter(item => !isExclusiveMaqOption(item)), option];
                    }
                } else if (question.type !== 'MTQ') {
                    // Single-select: clicking the already-selected option deselects it.
                    updatedQuestion.userAnswer = question.userAnswer === option ? null : option;
                }

                console.log('Updated question:', updatedQuestion);
                return updatedQuestion;
            });
        });
    };

    const handleFreeTextChange = (value) => {
        setQuestions(prevQuestions => prevQuestions.map((question, index) => (
            index === currentQuestionIndex ? { ...question, userAnswer: value } : question
        )));
    };

    // ui_rules USER_INPUT: the answer becomes { customInput, selectedIndex },
    // regardless of the question's own type — mirrors my-dq-web exactly.
    const handleCustomInputChange = (optionIndex, text) => {
        setQuestions(prevQuestions => prevQuestions.map((question, index) => (
            index === currentQuestionIndex
                ? { ...question, userAnswer: { customInput: text, selectedIndex: text ? optionIndex : null } }
                : question
        )));
    };

    const reorderRankedOption = (from, to) => {
        setQuestions(prevQuestions => {
            return prevQuestions.map((question, i) => {
                if (i !== currentQuestionIndex || question.type !== 'MTQ') return question;
                const order = question.userAnswer.slice();
                const [moved] = order.splice(from, 1);
                order.splice(to, 0, moved);
                return { ...question, userAnswer: order };
            });
        });
    };

    const handleDragPointerMove = (e) => {
        const from = dragInfo.current.index;
        if (from == null) return;
        const y = e.clientY;
        const to = rowRefs.current.findIndex((el) => {
            if (!el) return false;
            const rect = el.getBoundingClientRect();
            return y >= rect.top && y <= rect.bottom;
        });
        if (to === -1 || to === from) return;
        reorderRankedOption(from, to);
        dragInfo.current.index = to;
        setDragIndex(to);
    };

    const handleDragPointerUp = () => {
        dragInfo.current.index = null;
        setDragIndex(null);
        window.removeEventListener('pointermove', handleDragPointerMove);
        window.removeEventListener('pointerup', handleDragPointerUp);
    };

    const handleDragPointerDown = (e, index) => {
        e.preventDefault();
        dragInfo.current.index = index;
        setDragIndex(index);
        window.addEventListener('pointermove', handleDragPointerMove);
        window.addEventListener('pointerup', handleDragPointerUp);
    };

    const handleExit = async () => {
        // Reached from the Step 1 "Preview Assessments" onboarding card — exiting here
        // marks that step complete (unlocking Step 2) and returns to the workspace,
        // instead of the normal assessment-details page. Only for plans with
        // numbered/sequential steps — guards other users from an irrelevant write.
        // planHasSequentialSteps covers OWNER step sets only; a level-22 school
        // runs on CLIENT_ONBOARDING_STEPS_BY_PLAN, so it needs the client gate
        // too or its Step 1 is never recorded and Step 2 stays locked.
        if (location.state?.fromOnboarding
            && (planHasSequentialSteps(authUser?.dq_pro_plan?.plan_key) || isClientOnboardingUser(authUser))) {
            try {
                await updateUserDetailsService({ complete_onboarding_step: 1 });
                const res = await getUserDetailsService();
                const refreshedUser = res?.data?.user || res?.data;
                // Merge ONLY onboarding progress — a full replace drops
                // login-time session fields (see mergeOnboardingRefreshIntoAuthUser).
                const merged = mergeOnboardingRefreshIntoAuthUser(authUser, refreshedUser);
                if (merged !== authUser) setAuthUser(merged);
            } catch (err) {
                console.error('Failed to mark onboarding step complete:', err);
            } finally {
                history.push('/my_workspace');
            }
            return;
        }
        if (location.state?.fromOnboarding) {
            history.push('/my_workspace');
            return;
        }

        // The COS catalogue preview is opened from the COS Children Assessment
        // overview, so Exit returns there (7 Oct 2026).
        if (code === COS_PREVIEW_CODE) {
            history.push('/dq_pro/solutions/children-assessment/overview');
            return;
        }

        history.push('/dq_pro/assessment/list');
    };

    const fetchInstruments = async () => {
        setLoadingQuestions(true);
        try {
            // The COS catalogue preview has no built module behind it — ask for
            // the catalogue itself (no assessment is created, no credits spent).
            const response = code === COS_PREVIEW_CODE
                ? await getInsiderAssessmentPreviewService(null, { assessment_type: ASSESSMENT_TYPE.CPCI, user_group: USER_GROUPS.YOUNGPEOPLE })
                : await getInsiderAssessmentPreviewService(code);
            if (response?.data?.instruments) {
                console.log('API Response:', response.data);

                // Learn Creator — partner or direct (direct_lc sells the same solution).
                const isLcPlan = solutionPlanKey(authUser?.dq_pro_plan?.plan_key) === DQ_PRO_PLAN_KEY.ENTERPRISE_LC;
                const rawInstruments = isLcPlan
                    ? filterInstrumentsForLcPreview(response.data.instruments)
                    : response.data.instruments;

                const fetchedQuestions = rawInstruments.map((item, index) => {
                    // Section intro markers are interleaved by the backend before each
                    // section's questions — render them as intro cards, not questions.
                    if (item.type === 'section_intro') {
                        return {
                            id: index + 1,
                            type: 'section_intro',
                            section_code: item.section_code || '',
                            section_no: item.section_no || null,
                            title: item.title || '',
                            content: item.description || '',
                            components: Array.isArray(item.components) ? item.components : [],
                            question_count: item.question_count || 0,
                        };
                    }

                    let options = [];
                    try {
                        options = typeof item.instrument_response === 'string'
                            ? JSON.parse(item.instrument_response)
                            : item.instrument_response;

                        if (!Array.isArray(options)) {
                            options = [options];
                        }
                    } catch (error) {
                        console.error('Error parsing options:', error);
                        options = ['Option 1', 'Option 2']; // Fallback
                    }

                    return {
                        id: index + 1,
                        type: item.category_11 || 'MCQ',
                        instrument_question: item.instrument_question || 'No question text',
                        options: options,
                        question_subtitle_top: item.question_subtitle_top,
                        question_subtitle_bottom: item.question_subtitle_bottom,
                        ui_rules: item.ui_rules,
                        // MTQ (ranking/ordering): the answer IS an order, so it starts as
                        // the options' own indices in their given order (matching
                        // my-dq-web) rather than an empty/null selection — reordering
                        // moves indices around, the option TEXT is looked up by index
                        // wherever it's displayed.
                        userAnswer: item.userAnswer || (item.category_11 === 'MAQ' ? [] : item.category_11 === 'MTQ' ? options.map((_, i) => i) : null),
                        correctAnswer: '',
                    };
                });

                console.log('Formatted Questions:', fetchedQuestions);
                setQuestions(fetchedQuestions);
            }
        } catch (error) {
            console.error('Error fetching assessment questions:', error);
        } finally {
            setLoadingQuestions(false);
        }
    };

    useEffect(() => {
        fetchInstruments().then(r => console.log(r));
    }, [code])

    const handleNextQuestion = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const handlePreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const isOptionSelected = (option) => {
        if (!questions.length) return false;
        const currentQuestion = questions[currentQuestionIndex];

        if (!currentQuestion) return false;

        // Unified: MAQ is multi-select; every other type is single-select.
        if (currentQuestion.type === 'MAQ') {
            return currentQuestion.userAnswer?.includes(option) || false;
        }
        return currentQuestion.userAnswer === option;
        // Legacy:
        // if (currentQuestion.type === 'MCQ' || currentQuestion.type === 'LSQ') {
        //     return currentQuestion.userAnswer === option;
        // } else if (currentQuestion.type === 'MAQ') {
        //     return currentQuestion.userAnswer?.includes(option) || false;
        // }
        // return false;
    };

    const isFreeTextQuestion = (question) => {
        if (!question) return false;
        return question.type === 'OPQ' &&
            question.options.includes('Free text');
    };

    // An option no longer needs to be selected before advancing — Next stays
    // enabled regardless of whether the current question has an answer. The
    // last-question boundary is handled separately, at the call sites.
    const isNextDisabled = () => !questions.length;

    const calculateProgress = () => {
        return ((currentQuestionIndex + 1) / questions.length) * 100;
    };


    // MTQ: ranked/ordered list — drag handle + rank-number badge, reordered by
    // dragging (pointer events), since the answer is the order itself.
    const renderRankingOptions = (currentQuestion) => {
        const order = currentQuestion?.userAnswer;
        if (!Array.isArray(order)) return null;
        rowRefs.current = [];

        return (
            <Box sx={{ maxWidth: '960px', mx: 'auto', width: '100%', mt: 3 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-text-muted, #6B7280)', mb: 1 }}>
                    Drag and drop to order
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {order.map((optionIndex, i) => (
                        <Tooltip key={`${i}-${optionIndex}`} title="Drag to reorder" placement="top" arrow>
                            <Box
                                ref={(el) => { rowRefs.current[i] = el; }}
                                onPointerDown={(e) => handleDragPointerDown(e, i)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 2,
                                    p: '14px 20px', borderRadius: '12px',
                                    border: '1px solid var(--brand-border, #E5E7EB)',
                                    backgroundColor: 'var(--brand-surface, #ffffff)',
                                    opacity: dragIndex === i ? 0.5 : 1,
                                    boxShadow: dragIndex === i ? '0 4px 14px rgba(0,0,0,0.15)' : 'none',
                                    userSelect: 'none',
                                    cursor: dragIndex === i ? 'grabbing' : 'grab',
                                    touchAction: 'none',
                                    '&:hover': { borderColor: ACCENT },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                    <DragIndicatorIcon />
                                </Box>
                                <Box sx={{
                                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 700, fontSize: 15,
                                    backgroundColor: ACCENT, color: '#ffffff',
                                }}>
                                    {i + 1}
                                </Box>
                                <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 500, color: 'var(--brand-text, #111827)', textAlign: 'left' }}>
                                    {currentQuestion.options[optionIndex]}
                                </Typography>
                            </Box>
                        </Tooltip>
                    ))}
                </Box>
            </Box>
        );
    };

    // ── NEW: unified option rendering — every question type shares one clean
    //    full-width vertical list (lettered badge + text + check on select).
    const renderOptions = () => {
        if (questions.length === 0) return null;
        const currentQuestion = questions[currentQuestionIndex];
        if (!currentQuestion || !currentQuestion.options) return null;
        if (currentQuestion.type === 'MTQ') return renderRankingOptions(currentQuestion);

        if (isFreeTextQuestion(currentQuestion)) {
            return (
                <Box sx={{ maxWidth: '960px', mx: 'auto', width: '100%', mt: 3 }}>
                    <Box
                        component="textarea"
                        value={currentQuestion.userAnswer || ''}
                        onChange={(e) => handleFreeTextChange(e.target.value)}
                        placeholder="Type your answer here..."
                        sx={{
                            width: '100%', minHeight: 140, p: '14px 20px', borderRadius: '12px',
                            border: '1px solid var(--brand-border, #E5E7EB)',
                            backgroundColor: 'var(--brand-surface, #ffffff)',
                            fontSize: 16, fontFamily: 'inherit', color: 'var(--brand-text, #111827)',
                            resize: 'vertical', outline: 'none',
                            '&:focus': { borderColor: ACCENT },
                        }}
                    />
                </Box>
            );
        }

        const options = currentQuestion.options;
        const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

        return (
            <Box sx={{ maxWidth: '960px', mx: 'auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3 }}>
                {options.map((option, i) => {
                    // ui_rules USER_INPUT: an inline text field instead of a
                    // clickable option row, for this one option index.
                    if (inputIndices.includes(i)) {
                        const isCustomSelected = typeof currentQuestion.userAnswer === 'object'
                            && currentQuestion.userAnswer !== null
                            && currentQuestion.userAnswer.selectedIndex === i;
                        return (
                            <Box
                                key={i}
                                component="input"
                                value={isCustomSelected ? (currentQuestion.userAnswer.customInput || '') : ''}
                                onChange={(e) => handleCustomInputChange(i, e.target.value)}
                                placeholder={option}
                                sx={{
                                    width: '100%', p: '14px 20px', borderRadius: '12px',
                                    border: isCustomSelected ? `2px solid ${ACCENT}` : '1px solid var(--brand-border, #E5E7EB)',
                                    backgroundColor: 'var(--brand-surface, #ffffff)',
                                    fontSize: 16, fontFamily: 'inherit', color: 'var(--brand-text, #111827)',
                                    outline: 'none',
                                    '&:focus': { borderColor: ACCENT },
                                }}
                            />
                        );
                    }

                    const selected = isOptionSelected(option);
                    return (
                        <Box
                            key={i}
                            onClick={() => handleOptionSelect(option, i)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 2,
                                p: '14px 20px', borderRadius: '12px', cursor: 'pointer',
                                border: selected ? `2px solid ${ACCENT}` : '1px solid var(--brand-border, #E5E7EB)',
                                backgroundColor: selected ? 'rgba(255,105,0,0.06)' : 'var(--brand-surface, #ffffff)',
                                transition: 'all 0.15s ease',
                                '&:hover': { borderColor: ACCENT, boxShadow: '0px 2px 8px rgba(0,0,0,0.04)' },
                            }}
                        >
                            <Box sx={{
                                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: 15,
                                border: selected ? 'none' : '1.5px solid var(--brand-border, #D1D5DB)',
                                backgroundColor: selected ? ACCENT : 'transparent',
                                color: selected ? '#ffffff' : 'var(--brand-text-muted, #6B7280)',
                            }}>
                                {letters[i] || i + 1}
                            </Box>
                            <Typography sx={{ flex: 1, fontSize: 16, fontWeight: selected ? 700 : 500, color: 'var(--brand-text, #111827)', textAlign: 'left' }}>
                                {renderTextSegments(processedChoiceSegments[i] ?? option)}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // ── LEGACY renderer (kept for reference; no longer used). ──────────────────
    // eslint-disable-next-line no-unused-vars
    const renderOptionsLegacy = () => {
        if (questions.length === 0) return null;
        const currentQuestion = questions[currentQuestionIndex];
        if (!currentQuestion || !currentQuestion.options) return null;
console.log(currentQuestion.type );
        const options = currentQuestion.options;
        const isLSQ = currentQuestion.type === 'LSQ';
        const isMCQ = currentQuestion.type === 'MCQ';
        const isYN = options.every(opt =>
            ['Yes', 'No', 'Maybe'].includes(opt) && options.length <= 3
        );
        const isMAQ = currentQuestion.type === 'MAQ';

        if (isMAQ) {
            const isOdd = options.length % 2 !== 0;
            const midIndex = Math.floor(options.length / 2);

            const column1 = options.slice(0, midIndex);
            const column2 = options.slice(midIndex, isOdd ? options.length - 1 : undefined);
            const lastOption = isOdd ? options[options.length - 1] : null;

            return (
                <div className={classes.answerContainer}  style={{ height: embedded ? 'auto' : '42%' }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '16px',
                        width: '100%',
                        flexDirection:{xs:'column', sm:'row'}
                    }}>
                        {/* LEFT COLUMN */}
                        <div style={{
                            flex: 1,
                            maxWidth: '600px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: '16px',
                        }}>
                            {column1.map((option, index) => (
                                <div
                                    key={`left-${index}`}
                                    className={`${classes.optionItemBase} ${classes.maqOption} ${
                                        isOptionSelected(option) ? classes.selectedOption : ''
                                    }`}
                                    onClick={() => handleOptionSelect(option)}
                                >
                                    <Typography>{option}</Typography>
                                </div>
                            ))}
                        </div>

                        {/* RIGHT COLUMN */}
                        <div style={{
                            flex: 1,
                            maxWidth: '600px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: '16px',
                        }}>
                            {column2.map((option, index) => (
                                <div
                                    key={`right-${index}`}
                                    className={`${classes.optionItemBase} ${classes.maqOption} ${
                                        isOptionSelected(option) ? classes.selectedOption : ''
                                    }`}
                                    onClick={() => handleOptionSelect(option)}
                                >
                                    <Typography>{option}</Typography>
                                </div>
                            ))}
                        </div>
                    </Box>

                    {/* LAST ODD OPTION */}
                    {lastOption && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                            <div
                                className={`${classes.optionItemBase} ${classes.maqOption} ${
                                    isOptionSelected(lastOption) ? classes.selectedOption : ''
                                }`}
                                style={{ width: '100%', maxWidth: '1200px' }} // Full width
                                onClick={() => handleOptionSelect(lastOption)}
                            >
                                <Typography>{lastOption}</Typography>
                            </div>
                        </div>
                    )}
                </div>
                    );
        }

        // ── MCQ: CSS grid so the last item never stretches ───────────────────
        if (isMCQ) {
            // Detect long text — if avg option length > 40 chars, cap at 2 cols
            const avgLen = options.reduce((sum, o) => sum + o.length, 0) / options.length;
            const isLongText = avgLen > 40;

            let cols;
            if (isLongText) {
                // Long text: max 2 columns so cards don't become squarish
                cols = options.length <= 2 ? options.length : 2;
            } else if (options.length <= 4)      cols = options.length; // single row
            else if (options.length <= 6) cols = 3;              // 2 rows of 3
            else if (options.length <= 8) cols = 4;              // 2 rows of 4
            else                          cols = Math.ceil(options.length / 2);

            return (
                <div className={classes.answerContainer} style={{ height: embedded ? 'auto' : '42%' }}>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: `repeat(${cols}, 1fr)` },
                        gap: '16px',
                        width: '100%',
                    }}>
                        {options.map((option, i) => {
                            const isLastOdd = options.length % cols === 1 && i === options.length - 1;
                            return (
                                <div
                                    key={i}
                                    className={`${classes.optionItemBase} ${classes.lsqOption} ${isOptionSelected(option) ? classes.selectedOption : ''}`}
                                    onClick={() => handleOptionSelect(option)}
                                    style={isLastOdd ? {
                                        gridColumn: `1 / -1`,
                                        maxWidth: `calc((100% - ${(cols - 1) * 16}px) / ${cols})`,
                                        margin: '0 auto',
                                        width: '100%',
                                    } : {}}
                                >
                                    <Typography>{option}</Typography>
                                </div>
                            );
                        })}
                    </Box>
                </div>
            );
        }

        // ── Other types (LSQ, YN, OPQ) ───────────────────────────────────────
        let optionsPerRow;
        if (isLSQ) {
            optionsPerRow = Math.min(options.length, 5);
        } else if (isYN) {
            optionsPerRow = options.length;
        } else {
            optionsPerRow = Math.min(options.length, 3);
        }

        const rows = [];
        for (let i = 0; i < options.length; i += optionsPerRow) {
            rows.push(options.slice(i, i + optionsPerRow));
        }

        return (
            <div className={classes.answerContainer} style={{ height: embedded ? 'auto' : '42%' }}>
                {rows.map((row, rowIndex) => (
                    <Box
                        key={rowIndex}
                        className={classes.optionsRow}
                        sx={{
                            display:'flex',
                            justifyContent: 'center',
                            gap: '16px',
                            width: '100%',
                            flexDirection:{xs:'column', sm:'row'}
                        }}
                    >
                        {row.map((option, optionIndex) => (
                            <div
                                key={optionIndex}
                                className={`${classes.optionItemBase} ${isFreeTextQuestion(currentQuestion) ? classes.opqOption : ''} ${isLSQ ? classes.lsqOption : ''} ${isYN ? classes.ynOption : ''} ${isOptionSelected(option) ? classes.selectedOption : ''}`}
                                onClick={() => handleOptionSelect(option)}
                            >
                                <Typography>{option}</Typography>
                            </div>
                        ))}
                    </Box>
                ))}
            </div>
        );
    };

    return (
        <Box sx={{
            p: embedded ? 2 : 4,
            position: 'relative',
            minHeight: embedded ? 'auto' : '88vh',
            height: embedded ? '100%' : 'auto',
            display:'flex',
            flexDirection:'column',
            justifyContent:'flex-start',
            boxSizing: 'border-box',
        }}>
            {displayTooltip && (
                <Box
                    sx={{
                        position: 'fixed', top: tooltipPos.y + 14, left: tooltipPos.x + 14, zIndex: 2000,
                        maxWidth: 320, p: '10px 14px', borderRadius: '8px',
                        backgroundColor: '#1f2937', color: '#fff', fontSize: 13, lineHeight: 1.5,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)', pointerEvents: 'none',
                    }}
                    dangerouslySetInnerHTML={{ __html: tooltipText }}
                />
            )}
            <Typography variant="h3" sx={{
                textAlign: "center",
                marginBottom: {sm: 4, xs: 1},
                marginTop: {xs: '0px', sm: '0px'}
            }} className={classes.title}>
                Assessment Preview
            </Typography>

            {/*<LinearProgress*/}
            {/*    variant="determinate"*/}
            {/*    value={calculateProgress()}*/}
            {/*    className={classes.progressBar}*/}
            {/*/>*/}

            <div className={classes.questionContainer} style={{ maxWidth: embedded ? '1035px' : '1200px' }}>
                {!(loadingQuestions || questions.length === 0) && (
                questions[currentQuestionIndex].type === 'section_intro' ? (
                <Box sx={{ maxWidth: '960px', mx: 'auto', width: '100%' }}>
                    {/* Header: section number + title (with description below) */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
                        <Box sx={{
                            width: 48, height: 48, borderRadius: '12px', flexShrink: 0,
                            backgroundColor: 'rgba(255,105,0,0.10)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, color: ACCENT }}>
                                {questions[currentQuestionIndex].section_no
                                    || questions.slice(0, currentQuestionIndex + 1).filter(q => q.type === 'section_intro').length}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography sx={{ fontWeight: 700, color: '#C91C6E', fontSize: { xs: '22px', sm: '26px' }, lineHeight: 1.25 }}>
                                {questions[currentQuestionIndex].title}
                            </Typography>
                        </Box>
                    </Box>

                    {/* What you'll learn + section components */}
                    <Box sx={{
                        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '16px',
                        backgroundColor: 'var(--brand-surface, #ffffff)',
                        boxShadow: '0px 2px 12px rgba(0,0,0,0.05)',
                        p: { xs: 3, sm: 4 }, mb: 2.5,
                    }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--brand-text, #111827)', mb: 1 }}>
                            What you'll learn
                        </Typography>

                        {questions[currentQuestionIndex].content && (
                            <Typography sx={{ color: 'var(--brand-text-muted, #6B7280)', lineHeight: 1.6, mb: 3 }}>
                                {questions[currentQuestionIndex].content}
                            </Typography>
                        )}

                        {questions[currentQuestionIndex].components?.length > 0 && (
                            <>
                                <Box sx={{ height: '1px', backgroundColor: 'var(--brand-border, #F3F4F6)', mb: 3 }} />

                                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--brand-text-muted, #9CA3AF)', mb: 2 }}>
                                    Section Components
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(3, questions[currentQuestionIndex].components.length)}, 1fr)` }, gap: 2 }}>
                                    {questions[currentQuestionIndex].components.map((name) => (
                                        <Box key={name} sx={{
                                            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                                            backgroundColor: 'var(--brand-bg, #F9FAFB)', p: 2.25,
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                                <CheckIcon sx={{ fontSize: 18, color: ACCENT }} />
                                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                                                    {name}
                                                </Typography>
                                            </Box>
                                            {SECTION_COMPONENT_DESCRIPTIONS[name] && (
                                                <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)', lineHeight: 1.5 }}>
                                                    {SECTION_COMPONENT_DESCRIPTIONS[name]}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}
                    </Box>

                    {/*/!* Note *!/*/}
                    {/*<Box sx={{*/}
                    {/*    borderRadius: '12px',*/}
                    {/*    borderLeft: `4px solid ${ACCENT}`,*/}
                    {/*    backgroundColor: 'rgba(255,105,0,0.06)',*/}
                    {/*    p: '16px 20px', mb: 3,*/}
                    {/*}}>*/}
                    {/*    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 0.5 }}>*/}
                    {/*        Note:*/}
                    {/*    </Typography>*/}
                    {/*    <Typography sx={{ fontSize: 14, color: 'var(--brand-text-muted, #6B7280)', lineHeight: 1.6 }}>*/}
                    {/*        {sectionNoteText(questions[currentQuestionIndex].question_count)}*/}
                    {/*    </Typography>*/}
                    {/*</Box>*/}

                    {/* Back / Start Section */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            onClick={handlePreviousQuestion}
                            disabled={currentQuestionIndex === 0}
                            sx={{
                                textTransform: 'none', fontWeight: 700, fontSize: '15px', px: 3.5, py: 1.25, borderRadius: '10px',
                                color: 'var(--brand-text, #111827)', border: '1px solid var(--brand-border, #D1D5DB)',
                                '&:hover': { backgroundColor: 'var(--brand-bg, #F9FAFB)' },
                            }}
                        >
                            ← Back
                        </Button>
                        <Button
                            onClick={handleNextQuestion}
                            sx={{
                                textTransform: 'none', fontWeight: 700, fontSize: '15px', px: 4, py: 1.25, borderRadius: '10px',
                                backgroundColor: ACCENT, color: '#fff', boxShadow: 'none',
                                '&:hover': { backgroundColor: '#e55e00', boxShadow: 'none' },
                            }}
                        >
                            Start Section →
                        </Button>
                    </Box>
                </Box>
                ) : (
                <>
                {/* NEW: image-2 style header on a plain (no background) surface */}
                <Box sx={{ maxWidth: '960px', mx: 'auto', width: '100%', display: 'flex', alignItems: 'stretch', gap: { xs: 2, sm: 3 } }}>
                    {/* Question number */}
                    <Box sx={{
                        flexShrink: 0, textAlign: 'center',
                        pr: { xs: 2, sm: 3 }, borderRight: '2px solid var(--brand-border, #E5E7EB)',
                        display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                            Question
                        </Typography>
                        <Typography sx={{ fontSize: { xs: 34, sm: 44 }, fontWeight: 800, lineHeight: 1, color: ACCENT }}>
                            {String(realQuestionNumber).padStart(2, '0')}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                            of {loadingQuestions || realQuestionTotal === 0 ? '-' : realQuestionTotal}
                        </Typography>
                    </Box>

                    {/* Subtitle + question */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        {questions[currentQuestionIndex].question_subtitle_top && (
                            <Typography sx={{
                                display: 'inline-block', alignSelf: 'flex-start',
                                fontSize: 16, color: ACCENT, fontWeight: 600, textAlign: 'left',
                                backgroundColor: `color-mix(in srgb, ${ACCENT} 12%, transparent)`,
                                borderRadius: '6px', px: 1.25, py: 0.5, mb: 0.75, whiteSpace: 'pre-line',
                            }}>
                                {questions[currentQuestionIndex].question_subtitle_top}
                            </Typography>
                        )}
                        {(questions[currentQuestionIndex].instrument_question || questions[currentQuestionIndex].question) && (
                            <Typography sx={{ fontSize: { xs: '20px', sm: '24px' }, fontWeight: 800, color: 'var(--brand-text, #111827)', lineHeight: 1.3, textAlign: 'left' }}>
                                {renderTextSegments(processedQuestionSegments)}
                            </Typography>
                        )}
                        {questions[currentQuestionIndex].question_subtitle_bottom && (
                            <Typography sx={{ fontSize: 16,fontWeight: 600, color: '#666666ff', mt: 1, textAlign: 'left', whiteSpace: 'pre-line' }}>
                                {questions[currentQuestionIndex].question_subtitle_bottom}
                            </Typography>
                        )}
                    </Box>

                    {/* Right: circular progress with raaz icon */}
                    <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ position: 'relative', width: 76, height: 76 }}>
                            <CircularProgress variant="determinate" value={100} size={76} thickness={3.5}
                                sx={{ color: 'var(--brand-border, #F0F0F0)', position: 'absolute', left: 0, top: 0 }} />
                            <CircularProgress variant="determinate" value={calculateProgress()} size={76} thickness={3.5}
                                sx={{ color: ACCENT, position: 'absolute', left: 0, top: 0, '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {/* Raaz mascot — original SVG tinted with brand orange */}
                                <RazProgressSVG color="#FF6900" style={{ width: 42, height: 42 }} />
                            </Box>
                        </Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: ACCENT, mt: 0.5 }}>
                            {Math.round(calculateProgress())}%
                        </Typography>
                    </Box>
                </Box>

                {renderOptions()}

                {/* Previous / Next pill nav */}
                {!embedded && (
                    <Box sx={{ maxWidth: '960px', mx: 'auto', width: '100%', mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Button
                            onClick={handlePreviousQuestion}
                            disabled={currentQuestionIndex === 0}
                            sx={{
                                textTransform: 'none', fontWeight: 700, fontSize: '15px',
                                px: 3, py: 1.1, borderRadius: '999px',
                                color: 'var(--brand-text-muted, #6B7280)',
                                border: '1px solid var(--brand-border, #E5E7EB)',
                                '&:hover': { borderColor: ACCENT, color: ACCENT, backgroundColor: 'rgba(255,105,0,0.04)' },
                                '&.Mui-disabled': { opacity: 0.5 },
                            }}
                        >
                            ‹ Previous
                        </Button>
                        <Button
                            onClick={handleNextQuestion}
                            disabled={isNextDisabled() || currentQuestionIndex === questions.length - 1}
                            sx={{
                                textTransform: 'none', fontWeight: 700, fontSize: '15px',
                                px: 4, py: 1.1, borderRadius: '999px',
                                color: '#fff', boxShadow: 'none',
                                background: 'linear-gradient(to right, #E7466E, #FFAC46)',
                                '&:hover': { boxShadow: '0 4px 14px rgba(231,70,110,0.35)' },
                                '&.Mui-disabled': { background: 'var(--brand-border, #E5E7EB)', color: 'var(--brand-text-muted, #9CA3AF)' },
                            }}
                        >
                            Next ›
                        </Button>
                    </Box>
                )}

                {/* Exit (onboarding flow) */}
                {!embedded && (
                    <Box sx={{ maxWidth: '960px', mx: 'auto', width: '100%', mt: 2, textAlign: 'center' }}>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleExit()}
                        style={{
                                backgroundColor: '#ff6468',
                                color: '#ffffff',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                borderRadius: '8px',
                                padding: '9px 34px',
                            }}
                        >
                            Exit
                        </Button>
                    </Box>
                )}

                {/* LEGACY question header + options (kept, disabled) */}
                {false && (
                <>
                {questions[currentQuestionIndex].question_subtitle_top && (
                    <Typography variant="h6" className={classes.questionSubtitleTop}>
                        {questions[currentQuestionIndex].question_subtitle_top}
                    </Typography>
                )}

                {(questions[currentQuestionIndex].instrument_question || questions[currentQuestionIndex].question) && (
                <Typography variant="h4" className={classes.questionText}>
                    {questions[currentQuestionIndex].instrument_question || questions[currentQuestionIndex].question}
                </Typography>
                )}

                {questions[currentQuestionIndex].question_subtitle_bottom && (
                    <Typography variant="h6" className={classes.questionSubtitleBottom}>
                        {questions[currentQuestionIndex].question_subtitle_bottom}
                    </Typography>
                )}

                {renderOptionsLegacy()}
                </>
                )}
                </>
                )
                )}

            </div>

            {embedded && !loadingQuestions && questions.length > 0 && questions[currentQuestionIndex].type !== 'section_intro' && <div className={classes.navigationContainer}>
                <IconButton
                    style={{}}
                    className={classes.navButton}
                    onClick={handlePreviousQuestion}
                    disabled={currentQuestionIndex === 0}
                >
                    <ArrowBackIosIcon className={classes.navIcon} />
                </IconButton>

                <IconButton
                    className={classes.navButton}
                    onClick={() => {
                        if (currentQuestionIndex === questions.length - 1) {
                            console.log('assessment completed');
                            // history.push('/dq_pro/products/assessments/result');
                        } else {
                            handleNextQuestion();
                        }
                    }}
                    disabled={isNextDisabled()}
                >
                    <ArrowForwardIosIcon className={classes.navIcon} />
                </IconButton>
            </div>}
        </Box>
    );
};

export default DQProPreviewAssessment;